import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, FlaskConical, CheckCircle } from 'lucide-react';
import HaleAdminLayout from '../../components/hale/HaleAdminLayout';
import { T, FONT, ConsoleCard, Eyebrow, StatusPill, InfoRow, Spinner } from '../hale-console/_theme';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { key: 'under_review',          label: 'Under Review' },
  { key: 'information_requested', label: 'Information Requested' },
  { key: 'approved',              label: 'Hale Reviewed Approved' },
  { key: 'conditional_approval',  label: 'Conditional Approval' },
  { key: 'not_approved',          label: 'Not Approved' },
];

export default function HaleReviewedProduct() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/hale-reviewed/admin/products/${id}`, { withCredentials: true });
      setP(r.data);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    setBusy(status);
    try {
      const r = await axios.patch(`${API}/api/hale-reviewed/admin/products/${id}/status`, { status, note: note || null }, { withCredentials: true });
      setP(r.data); setNote('');
    } finally { setBusy(''); }
  };

  return (
    <HaleAdminLayout>
      <Link to="/admin/hale-reviewed" className="inline-flex items-center gap-1.5 text-[12px] font-semibold mb-6" style={{ color: T.MUTED }} data-testid="hale-product-back">
        <ArrowLeft size={14} /> Back to queue
      </Link>

      {loading && <Spinner label="Loading product…" />}

      {!loading && p && (
        <div className="space-y-5">
          {/* Product overview */}
          <ConsoleCard data-testid="hale-product-overview">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <Eyebrow>Hale Reviewed · Product overview</Eyebrow>
                <h1 className="text-[clamp(1.5rem,3vw,2.1rem)] font-black tracking-[-0.02em]" style={{ fontFamily: FONT.DISPLAY, color: T.INK }} data-testid="hale-product-name">{p.product_name}</h1>
              </div>
              <StatusPill status={p.status} testid="hale-product-status" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 mt-4">
              <InfoRow label="Brand" value={p.brand_name} />
              <InfoRow label="Category" value={p.category} />
              <InfoRow label="Submission date" value={p.submitted_at ? new Date(p.submitted_at).toLocaleString() : '—'} />
              <InfoRow label="Marketplace eligible" value={p.marketplace_eligible ? 'Yes' : 'No'} />
            </div>
          </ConsoleCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* INCI */}
            <ConsoleCard data-testid="hale-product-inci">
              <div className="flex items-center gap-2 mb-3"><FlaskConical size={16} style={{ color: T.RED }} /><p className="text-[14px] font-bold" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>INCI list</p></div>
              <p className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: T.SOFT }}>{p.inci_list || '—'}</p>
            </ConsoleCard>

            {/* Claims */}
            <ConsoleCard data-testid="hale-product-claims">
              <div className="flex items-center gap-2 mb-3"><CheckCircle size={16} style={{ color: T.RED }} /><p className="text-[14px] font-bold" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>Claims</p></div>
              {p.claims?.length ? (
                <ul className="space-y-2">{p.claims.map((c, i) => <li key={i} className="text-[13px] flex gap-2" style={{ color: T.SOFT }}><span style={{ color: T.RED }}>•</span> {c}</li>)}</ul>
              ) : <p className="text-[13px]" style={{ color: T.MUTED }}>No claims submitted.</p>}
            </ConsoleCard>

            {/* Documents */}
            <ConsoleCard data-testid="hale-product-documents">
              <div className="flex items-center gap-2 mb-3"><FileText size={16} style={{ color: T.RED }} /><p className="text-[14px] font-bold" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>Supporting documents</p></div>
              {p.documents?.length ? (
                <ul className="space-y-2">{p.documents.map((d, i) => (
                  <li key={i} className="text-[13px]" style={{ color: T.SOFT }}>
                    <span className="font-semibold" style={{ color: T.INK }}>{d.name}</span>{d.note ? ` — ${d.note}` : ''}
                  </li>
                ))}</ul>
              ) : <p className="text-[13px]" style={{ color: T.MUTED }}>No documents provided.</p>}
            </ConsoleCard>

            {/* Status history */}
            <ConsoleCard data-testid="hale-product-history">
              <Eyebrow>Status history</Eyebrow>
              <ul className="space-y-3">{(p.status_history || []).slice().reverse().map((h, i) => (
                <li key={i} className="text-[12.5px]">
                  <div className="flex items-center gap-2"><StatusPill status={h.status} /><span style={{ color: T.MUTED }}>{h.at ? new Date(h.at).toLocaleDateString() : ''}</span></div>
                  {h.note && <p className="mt-1 ml-1" style={{ color: T.SOFT }}>{h.note}</p>}
                  {h.by && <p className="text-[11px] mt-0.5" style={{ color: T.MUTED }}>by {h.by}</p>}
                </li>
              ))}</ul>
            </ConsoleCard>
          </div>

          {/* Decision / status controls */}
          <ConsoleCard style={{ backgroundColor: T.INK }} data-testid="hale-product-controls">
            <p className="text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: T.RED, fontFamily: FONT.MONO }}>Update Hale Reviewed status</p>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to the brand (shown in their portal)…" rows={2}
              className="w-full rounded-lg px-3.5 py-2.5 text-[13px] mb-4 outline-none" style={{ backgroundColor: T.INK2, color: T.WHITE, border: '1px solid #2A2B30' }} data-testid="hale-product-note" />
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button key={s.key} onClick={() => setStatus(s.key)} disabled={!!busy}
                  data-testid={`hale-product-set-${s.key}`}
                  className="px-4 py-2.5 rounded-lg text-[12px] font-semibold disabled:opacity-50"
                  style={{ backgroundColor: s.key === 'approved' ? T.RED : 'rgba(255,255,255,0.08)', color: T.WHITE, border: s.key === 'approved' ? 'none' : '1px solid rgba(255,255,255,0.18)' }}>
                  {busy === s.key ? 'Saving…' : s.label}
                </button>
              ))}
            </div>
          </ConsoleCard>
        </div>
      )}
    </HaleAdminLayout>
  );
}
